import React from 'react';
import { Link } from 'react-router-dom';
import ProductCard from './ProductCard';
import { Product } from '../../types/product';
import { ProductItem } from '../../services/product.service';

// Mapper function to convert Product to ProductItem
const mapToProductItem = (product: Product): ProductItem => {
  return {
    id: product.id,
    name: product.name,
    description: product.description || '',
    category: '',
    images: product.images || [],
    unit: product.unit || '',
    original_price: product.original_price || 0,
    current_price: product.original_price || 0,
    is_on_sale: product.is_on_sale || false,
    is_trending: product.is_trending || false,
    status: product.status,
    type: (product as any).type,
    variants: (product as any).variants,
    sale_price: (product as any).sale_price,
  };
};

export interface ProductGridProps {
  products: Product[];
  title?: string;
  viewAllLink?: string;
  bgColor?: string;
  homeLayout?: boolean;
  showEvenIfEmpty?: boolean;
  isHot?: boolean;
}

const ProductGrid: React.FC<ProductGridProps> = ({
  products,
  title,
  viewAllLink,
  bgColor = 'bg-background',
  homeLayout = false,
  showEvenIfEmpty = false,
  isHot = false,
}) => {
  if (!products || products.length === 0) {
    if (showEvenIfEmpty && title) {
      // Show category title even if no products
      return (
        <section className={`py-12 ${bgColor}`}>
          <div className='container mx-auto px-2 sm:px-4'>
            {/* Desktop layout - title and "Xem tất cả" on same row */}
            <div className='hidden sm:flex flex-row justify-between items-center mb-8'>
              <div className='flex items-center space-x-3'>
                <h2 className='text-2xl font-bold text-primary relative inline-block'>
                  {title}
                  <span className='absolute -bottom-1 left-0 right-0 h-1 bg-primary/20 rounded-full'></span>
                </h2>
                {isHot && (
                  <div className='flex items-center space-x-1 bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full shadow-lg animate-pulse'>
                    <span className='text-lg'>🔥</span>
                    <span className='text-sm font-bold'>HOT</span>
                    <span className='text-lg animate-bounce'>⭐</span>
                  </div>
                )}
              </div>
              {viewAllLink && (
                <Link
                  to={viewAllLink}
                  className='text-primary font-medium hover:text-primary-dark flex items-center'
                >
                  Xem tất cả
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-4 w-4 ml-1'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 5l7 7-7 7'
                    />
                  </svg>
                </Link>
              )}
            </div>

            {/* Mobile layout - title only */}
            <div className='sm:hidden mb-8'>
              <div className='flex items-center space-x-2'>
                <h2 className='text-2xl font-bold text-primary relative inline-block'>
                  {title}
                  <span className='absolute -bottom-1 left-0 right-0 h-1 bg-primary/20 rounded-full'></span>
                </h2>
                {isHot && (
                  <div className='flex items-center space-x-1 bg-gradient-to-r from-red-500 to-orange-500 text-white px-2 py-1 rounded-full shadow-lg animate-pulse'>
                    <span className='text-sm'>🔥</span>
                    <span className='text-xs font-bold'>HOT</span>
                    <span className='text-sm animate-bounce'>⭐</span>
                  </div>
                )}
              </div>
            </div>

            <div className='text-center py-8 text-gray-500'>
              Chưa có sản phẩm trong danh mục này
            </div>

            {/* Mobile layout - "Xem tất cả" below message */}
            {viewAllLink && (
              <div className='sm:hidden flex justify-center mt-6'>
                <Link
                  to={viewAllLink}
                  className='text-primary font-medium hover:text-primary-dark flex items-center'
                >
                  Xem tất cả
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-4 w-4 ml-1'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 5l7 7-7 7'
                    />
                  </svg>
                </Link>
              </div>
            )}
          </div>
        </section>
      );
    }
    return null;
  }

  // For home layout, limit to 12 products (2 rows of 6)
  const displayProducts = homeLayout ? products.slice(0, 12) : products;

  // console.log('displayProducts ', displayProducts);

  return (
    <section className={`py-12 ${bgColor}`}>
      <div className='container mx-auto px-2 sm:px-4'>
        {/* Desktop layout - title and "Xem tất cả" on same row */}
        <div className='hidden sm:flex flex-row justify-between items-center mb-8'>
          {title && (
            <div className='flex items-center space-x-3'>
              <h2 className='text-2xl font-bold text-primary relative inline-block'>
                {title}
                <span className='absolute -bottom-1 left-0 right-0 h-1 bg-primary/20 rounded-full'></span>
              </h2>
              {isHot && (
                <div className='flex items-center space-x-1 bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full shadow-lg animate-pulse'>
                  <span className='text-lg'>🔥</span>
                  <span className='text-sm font-bold'>HOT</span>
                  <span className='text-lg animate-bounce'>⭐</span>
                </div>
              )}
            </div>
          )}
          {viewAllLink && (
            <Link
              to={viewAllLink}
              className='text-primary font-medium hover:text-primary-dark flex items-center'
            >
              Xem tất cả
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-4 w-4 ml-1'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 5l7 7-7 7'
                />
              </svg>
            </Link>
          )}
        </div>

        {/* Mobile layout - title only */}
        {title && (
          <div className='sm:hidden mb-8'>
            <div className='flex items-center space-x-2'>
              <h2 className='text-2xl font-bold text-primary relative inline-block'>
                {title}
                <span className='absolute -bottom-1 left-0 right-0 h-1 bg-primary/20 rounded-full'></span>
              </h2>
              {isHot && (
                <div className='flex items-center space-x-1 bg-gradient-to-r from-red-500 to-orange-500 text-white px-2 py-1 rounded-full shadow-lg animate-pulse'>
                  <span className='text-sm'>🔥</span>
                  <span className='text-xs font-bold'>HOT</span>
                  <span className='text-sm animate-bounce'>⭐</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div
          className={`grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-4 md:gap-6`}
        >
          {displayProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={mapToProductItem(product)}
            />
          ))}
        </div>

        {/* Mobile layout - "Xem tất cả" below products */}
        {viewAllLink && (
          <div className='sm:hidden flex justify-center mt-6'>
            <Link
              to={viewAllLink}
              className='text-primary font-medium hover:text-primary-dark flex items-center'
            >
              Xem tất cả
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-4 w-4 ml-1'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 5l7 7-7 7'
                />
              </svg>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductGrid;
